import React, { useState, useCallback, useEffect, useImperativeHandle } from 'react';
import { Input, Tree, Divider } from 'antd';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import { List as VList } from 'react-virtualized/dist/commonjs/List';
import { apiHolder } from 'api';
import classnames from 'classnames';
import SearchIcon from '../Icons/svgs/SearchSvg';
import styles from './siriusContact.module.scss';
import { StaticNodeKey } from '@web-common/utils/contact_util';
import { getIn18Text } from 'api';
const nimApi = apiHolder.api.requireLogicalApi('NIM');
export var TreeType;
(function (TreeType) {
    TreeType[TreeType["PERSON"] = 0] = "PERSON";
    TreeType[TreeType["ORG"] = 1] = "ORG";
    TreeType[TreeType["Group"] = 2] = "Group";
})(TreeType || (TreeType = {}));
export const SiriusContact = React.forwardRef((props, ref) => {
    const { onSearch, keyword, searchListItemRender, searchListItemHeight, searchListCount, renderPersonTitle, renderOrgTitle, renderGroupTitle, onLoadData, onGroupLoadData, personData, orgData, groupData, selectedPersonKeys = [], selectedOrgKeys = [], containerClassName, personExpand, personNodeSelect, nodeSelect, OrgExpand, GroupExpand, shouldShowOrgContact = true, } = props;
    const [loadedKeys, setLoadedKeys] = useState([]);
    const [expandedPersonKeys, setExpandedPersonKeys] = useState([StaticNodeKey.PERSON]);
    const [expandedOrgKeys, setExpandedOrgKeys] = useState([StaticNodeKey.CORP]);
    const [expandedGroupKeys, setExpandedGroupKeys] = useState([StaticNodeKey.GROUP]);
    const [searchValue, setSearchValue] = useState('');
    const inputChange = useCallback((e) => {
        const { value } = e.target;
        setSearchValue(value);
        onSearch && onSearch(value);
    }, [onSearch]);
    const handlePersonExpand = useCallback(expendKeys => {
        personExpand && personExpand(expendKeys);
        setExpandedPersonKeys(expendKeys);
    }, []);
    const handleOrgExpand = useCallback(expendKeys => {
        OrgExpand && OrgExpand(expendKeys.length > expandedOrgKeys.length);
        setExpandedOrgKeys(expendKeys);
    }, []);
    const handleGroupExpand = useCallback(expendKeys => {
        GroupExpand && GroupExpand(expendKeys.length > expandedGroupKeys.length);
        setExpandedGroupKeys(expendKeys);
    }, []);
    const loadData = node => {
        const { key } = node;
        if (loadedKeys.indexOf(String(key)) !== -1) {
            return Promise.resolve();
        }
        setLoadedKeys(prevLoadedKeys => [].concat(prevLoadedKeys, String(key)));
        return (onLoadData && onLoadData(node)) || Promise.resolve();
    };
    const loadGroupData = (node) => {
        const { key } = node;
        if (loadedKeys.indexOf(String(key)) !== -1) {
            return Promise.resolve();
        }
        setLoadedKeys(prevLoadedKeys => [].concat(prevLoadedKeys, String(key)));
        return (onGroupLoadData && onGroupLoadData(node)) || Promise.resolve();
    };
    const onNodeSelect = (key, type) => {
        let keys;
        let setExpandedKeys;
        if (type === TreeType.PERSON) {
            keys = expandedPersonKeys;
            setExpandedKeys = setExpandedPersonKeys;
        }
        else if (type === TreeType.ORG) {
            keys = expandedOrgKeys;
            setExpandedKeys = setExpandedOrgKeys;
        }
        else {
            keys = expandedGroupKeys;
            setExpandedKeys = setExpandedGroupKeys;
        }
        let expanedKeys;
        if (keys.includes(key)) {
            expanedKeys = keys.filter(elem => elem !== key);
        }
        else {
            expanedKeys = [...keys, key];
        }
        setExpandedKeys(expanedKeys);
    };
    const onPersonNodeSelect = (_, e) => {
        personNodeSelect && personNodeSelect();
        onNodeSelect(e.node.key, TreeType.PERSON);
    };
    const onOrgNodeSelect = (_, e) => {
        if (e.node.isLeaf) {
            return;
        }
        onNodeSelect(e.node.key, TreeType.ORG);
        nodeSelect && nodeSelect();
        if (loadedKeys.indexOf(String(e.node.key)) === -1) {
            loadData(e.node);
        }
    };
    const onGroupNodeSelect = (_, e) => {
        if (e.node.isLeaf) {
            return;
        }
        onNodeSelect(e.node.key, TreeType.Group);
        nodeSelect && nodeSelect();
        if (loadedKeys.indexOf(String(e.node.key)) === -1) {
            loadGroupData(e.node);
        }
    };
    const visibleGroup = nimApi.getIMAuthConfig() && (groupData === null || groupData === void 0 ? void 0 : groupData.some(item => { var _a; return Boolean((_a = item.children) === null || _a === void 0 ? void 0 : _a.length); }));
    useImperativeHandle(ref, () => ({
        resetContactTree: () => {
            setLoadedKeys([]);
        },
    }));
    useEffect(() => {
        if (typeof keyword !== 'undefined' && keyword !== searchValue) {
            setSearchValue(keyword);
        }
    }, [keyword]);
    return (React.createElement("div", { className: "sirius-contact-tree-container" },
        React.createElement("div", { className: styles.searchContainer },
            React.createElement(Input, { placeholder: getIn18Text('SOUSUOLIANXIREN'), prefix: React.createElement(SearchIcon, null), value: searchValue, allowClear: true, onChange: inputChange })),
        searchValue ? (React.createElement("div", { className: styles.contactListWrapper },
            React.createElement(AutoSizer, null, ({ width, height }) => (React.createElement(VList, { height: height, width: width, rowCount: searchListCount || 0, rowHeight: searchListItemHeight, className: "sirius-scroll-hide", rowRenderer: searchListItemRender }))))) : (React.createElement("div", { className: styles.treeWrapper },
            React.createElement("div", { className: classnames(styles.treeContainer, containerClassName) },
                React.createElement(Tree, { motion: false, blockNode: true, treeData: personData, titleRender: renderPersonTitle, expandedKeys: expandedPersonKeys, onExpand: handlePersonExpand, onSelect: onPersonNodeSelect, multiple: true, selectedKeys: selectedPersonKeys })),
            shouldShowOrgContact && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles.dividerContainer },
                    React.createElement(Divider, null)),
                React.createElement("div", { className: classnames(styles.treeContainer, containerClassName) },
                    React.createElement(Tree, { motion: false, blockNode: true, treeData: orgData, titleRender: renderOrgTitle, expandedKeys: expandedOrgKeys, onExpand: handleOrgExpand, loadedKeys: loadedKeys, loadData: loadData, onSelect: onOrgNodeSelect, multiple: true, selectedKeys: selectedOrgKeys })))),
            visibleGroup ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles.dividerContainer },
                    React.createElement(Divider, null)),
                React.createElement("div", { className: classnames(styles.treeContainer, containerClassName) },
                    React.createElement(Tree, { blockNode: true, treeData: groupData, expandedKeys: expandedGroupKeys, titleRender: renderGroupTitle, onSelect: onGroupNodeSelect, onExpand: handleGroupExpand, loadedKeys: loadedKeys, loadData: loadGroupData, multiple: true, selectedKeys: selectedOrgKeys })))) : ('')))));
});
export default SiriusContact;
